/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { OpenAPIHousien } from "@services/requests/core/OpenAPI";
import { request as __request } from "@services/requests/core/request";

export interface KpiItem {
  value: number;
  delta: number;
  direction: "up" | "down" | "neutral";
}

export interface FunnelStage {
  count: number;
  last_client: string;
}

export interface DashboardVisit {
  id: string;
  property_title: string;
  client_name: string;
  time: string;
  status: "confirmed" | "pending";
}

export interface DashboardProperty {
  id: string;
  title: string;
  category: string;
  transaction_type: string;
  price: number;
}

export interface ActivityItem {
  id: string;
  timestamp: string;
  type: "lead" | "visit" | "property" | string;
  subject: string;
}

export interface ClientWithoutReturn {
  client_id: string;
  client_name: string;
  /** Null for a client with no attendance at all: no wait to measure. */
  days_since_contact: number | null;
  stage: string;
}

export type ComparisonPeriod = "dod" | "wow" | "mom" | "qoq" | "yoy";

export interface LossReasonItem {
  reason: string;
  count: number;
}

/** One rent expectation past its due date with no payment recorded. */
export interface OverdueRentItem {
  lease_id: string;
  accrual_period: string;
  due_date: string;
  days_late: number;
  amount_cents: number;
}

/** A running contract ending inside the window. Negative when already past. */
export interface ExpiringContractItem {
  lease_id: string;
  property_id: string;
  end_date: string;
  days_until_end: number;
}

/**
 * Facts that name a listing whose headline was never written.
 *
 * They arrive as facts and not as a sentence: joining them into
 * "Apartamento · 2 quartos · 44 m²" is the frontend's job, because the
 * words between them differ per language.
 */
export interface ListingIdentity {
  /** "" when the listing carries no category. */
  category: string;
  bedrooms: number | null;
  area: number | null;
}

/** A listing that cannot be published as it stands. */
export interface IncompleteListingItem extends ListingIdentity {
  id: string;
  title: string;
  /** Publication blockers, in reading order. */
  missing: Array<"photo" | "price" | "address">;
}

/** An on-market listing nobody has visited inside the window. */
export interface StaleVisitItem {
  id: string;
  title: string;
  /** Null when the property has never been visited: no wait to measure. */
  days_since_visit: number | null;
}

/**
 * A section carrying a complete count beside a capped preview of it.
 * The count is the truth; `items` is the few the card has room for.
 */
export interface CountedSection<TItem> {
  count: number;
  items: TItem[];
}

export interface VacancySection {
  vacant: number;
  total: number;
  /** Null for an empty portfolio, where zero percent would read as full. */
  rate_pct: number | null;
  average_days: number | null;
}

export interface ExpiringContractsSection
  extends CountedSection<ExpiringContractItem> {
  window_days: number;
  /** Contracts this count could not cover: the term is optional. */
  without_end_date: number;
}

export interface SalesVolumeSection {
  available: number;
  /** Listings that could not contribute to `available`. */
  without_price: number;
  realized_month: number;
  realized_count: number;
}

export interface DashboardResponse {
  kpis: {
    visits_this_week: KpiItem;
    leads_without_return: KpiItem;
    conversion_rate: KpiItem;
    loss_reasons?: LossReasonItem[];
  };
  sales_funnel: {
    active_leads: FunnelStage;
    in_negotiation: FunnelStage;
    proposal_sent: FunnelStage;
    closed_this_month: FunnelStage;
  };
  visits_today: DashboardVisit[];
  properties: DashboardProperty[];
  recent_activity: ActivityItem[];
  clients_without_return: ClientWithoutReturn[];
  /** Properties per category across the whole portfolio, not the top four. */
  category_counts: Record<string, number>;
  /**
   * How many properties the agency has. Deliberately not the sum of
   * `category_counts`: that histogram only counts categories in the allowed
   * vocabulary, so a listing with a null or off-vocabulary category is
   * missing from it. Null when statistics have never been computed, which is
   * not the same as an empty book.
   */
  total_properties: number | null;
  /** Open attendances per running pipeline stage, one key per stage. */
  attendance_by_stage: Record<string, number>;
  new_listings: { count: number; window_days: number };
  incomplete_listings: CountedSection<IncompleteListingItem>;
  properties_without_recent_visits: CountedSection<StaleVisitItem> & {
    window_days: number;
  };
  overdue_rent: CountedSection<OverdueRentItem> & { total_cents: number };
  vacancy: VacancySection;
  /** Null when nothing has closed yet, which is not the same as zero days. */
  average_stock_days: number | null;
  expiring_contracts: ExpiringContractsSection;
  sales_volume: SalesVolumeSection;
  /** When the materialized aggregate behind this payload was computed. */
  computed_at?: string;
  /**
   * Present, and `"me"`, only when the payload was narrowed to the signed-in
   * member. The agency payload has no such key: the screen reads it to know
   * whose numbers it is showing, not to ask for them.
   */
  scope?: DashboardScope;
}

/** Whose numbers a payload carries. Absent means the whole agency. */
export type DashboardScope = "me";

/**
 * Fetches the aggregated dashboard payload for an agency from
 * `GET /agencies/{agencyId}/dashboard`, optionally scoped to a comparison period.
 *
 * @param agencyId - The agency whose dashboard metrics are requested
 * @param comparison - Optional period used to compute period-over-period deltas
 * @param scope - `"me"` narrows every number to the signed-in member; the
 *   identity comes from the token, so there is nothing to pass but the intent
 * @returns The dashboard funnel, visits, properties, portfolio and activity
 */
export function fetchDashboard(
  agencyId: string,
  comparison?: ComparisonPeriod,
  scope?: DashboardScope,
): Promise<DashboardResponse> {
  const query = {
    ...(comparison ? { comparison } : {}),
    ...(scope ? { scope } : {}),
  };
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agencies/{agencyId}/dashboard",
    path: { agencyId },
    query: Object.keys(query).length ? query : undefined,
  });
}

/**
 * Recomputes the agency's overview server-side and returns the fresh payload.
 *
 * For the staleness no write can clear: the dashboard is a materialized
 * snapshot, and re-fetching it hands back the same copy. The recompute also
 * rewrites the agency's statistics document, which nothing but a property
 * write otherwise touches — that is what the portfolio block reads.
 *
 * Throttled server-side: within a minute of the last computation the current
 * payload comes back untouched rather than rescanning every property shard.
 *
 * @param agencyId - The agency to recompute
 * @returns The freshly computed dashboard payload
 */
export function refreshDashboard(agencyId: string): Promise<DashboardResponse> {
  return __request(OpenAPIHousien, {
    method: "POST",
    url: "/agencies/{agencyId}/dashboard/refresh",
    path: { agencyId },
  });
}

export type BlockSpan = "full" | "half";

export interface DashboardBlockPreference {
  id: string;
  visible: boolean;
  span: BlockSpan;
}

/**
 * How much of the overview a person wants on screen.
 *
 * Defaults to `advanced` everywhere it is missing or unrecognised — falling
 * back to more information costs a scroll, falling back to less would hide
 * numbers somebody was relying on.
 */
export type OverviewMode = "advanced" | "basic";

/** Whose overview: the agency's, or a broker's own (`?scope=me`). */
export type OverviewView = "agency" | "me";

/**
 * The cards a person chose, in render order, for each view and mode.
 *
 * An empty list means they never chose, and the client shows its own
 * default -- the server keeps the vocabulary and the limits, not the
 * default set.
 */
export type OverviewTiles = Record<OverviewView, Record<OverviewMode, string[]>>;

export interface DashboardPreferences {
  /** Array order is render order. */
  blocks: DashboardBlockPreference[];
  /** How much of the overview this user wants. */
  mode?: OverviewMode;
  /**
   * The four modules in this user's mobile bottom bar, in order. The fifth
   * cell ("Mais") is fixed and never stored. Empty means they never chose,
   * and the client answers that with its own default bar — the server has
   * no vocabulary of nav ids, because that list is feature-flagged and
   * role-filtered on this side.
   */
  mobile_bar?: string[];
  /** The cards this user chose, per view and per mode. */
  tiles?: OverviewTiles;
  /** Server-owned: whether a block means anything for this agency at all. */
  applicability: { launches?: boolean };
  updated_at?: string;
  updated_by?: string;
}

/**
 * Fetches the calling user's own overview layout for an agency.
 *
 * Never 404s: a user who has never personalised the page gets the defaults.
 *
 * @param agencyId - The agency whose layout is requested
 * @returns The user's blocks in render order, plus what applies to the agency
 */
export function fetchDashboardPreferences(
  agencyId: string,
): Promise<DashboardPreferences> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agencies/{agencyId}/dashboard-preferences",
    path: { agencyId },
  });
}

/**
 * Stores how much of the overview this user wants, and nothing else.
 *
 * The server keeps every field the body omits, so switching mode never
 * touches the cards somebody picked or their mobile bar.
 *
 * @param agencyId - The agency whose preferences are being saved
 * @param mode - Basic or advanced
 * @returns The preferences as they will be read back
 */
export function saveOverviewMode(
  agencyId: string,
  mode: OverviewMode,
): Promise<DashboardPreferences> {
  return __request(OpenAPIHousien, {
    method: "PUT",
    url: "/agencies/{agencyId}/dashboard-preferences",
    path: { agencyId },
    body: { mode },
  });
}

/**
 * Stores the cards this user chose for the overview.
 *
 * The whole document goes, all four lists: the picker holds every one of
 * them, and the server refuses an unknown card, a repeat, or a list over
 * its mode's limit with a 400.
 *
 * @param agencyId - The agency whose preferences are being saved
 * @param tiles - Every view and mode, each list in render order
 * @returns The preferences as they will be read back
 */
export function saveOverviewTiles(
  agencyId: string,
  tiles: OverviewTiles,
): Promise<DashboardPreferences> {
  return __request(OpenAPIHousien, {
    method: "PUT",
    url: "/agencies/{agencyId}/dashboard-preferences",
    path: { agencyId },
    body: { tiles },
  });
}

/**
 * Stores the mobile bottom bar without touching the overview layout.
 *
 * The shortcut editor has no idea what blocks the person arranged, and
 * sending a default layout alongside the bar would quietly overwrite one
 * they had. The server keeps every field the body omits, so this writes
 * exactly the one thing the editor knows about.
 *
 * @param agencyId - Agency whose preferences document is being written
 * @param bar - Four distinct nav item ids, in bar order; `[]` clears the
 *   choice and hands the default back to the client
 */
export function saveMobileBar(
  agencyId: string,
  bar: string[],
): Promise<DashboardPreferences> {
  return __request(OpenAPIHousien, {
    method: "PUT",
    url: "/agencies/{agencyId}/dashboard-preferences",
    path: { agencyId },
    body: { mobile_bar: bar },
  });
}
